"use client";
import * as React from "react";
import { useState, useEffect } from "react";

function CareersHeroSection() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section
      className="relative w-full overflow-hidden pt-[100px] pb-[80px] bg-white font-['Poppins',sans-serif]"
      role="banner"
      aria-label="Careers hero section"
    >
      {/* Wave Background System */}
      <div className="absolute inset-0 w-full h-full">
        {/* Base Layer - Blue Gradient */}
        <div
          className="absolute top-0 left-0 h-full w-full bg-gradient-to-br from-[#1719A8] to-[#0F1280]"
          aria-hidden="true"
        />

        {/* First Wave Layer - Light Blue with Multiple Peaks */}
        <svg
          className="absolute top-0 left-0 h-full w-full opacity-70"
          viewBox="0 0 1200 600"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="firstWave" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#60A5FA" stopOpacity="0.6" />
              <stop offset="50%" stopColor="#93C5FD" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#DBEAFE" stopOpacity="0.2" />
            </linearGradient>
          </defs>
          <path
            d="M0,0 L1200,0 L1200,200
               C1100,250 1000,180 900,220
               C800,260 700,190 600,230
               C500,270 400,200 300,240
               C200,280 100,210 0,250 Z"
            fill="url(#firstWave)"
          />
        </svg>

        {/* Second Wave Layer - Medium Blue with Different Pattern */}
        <svg
          className="absolute top-0 left-0 h-full w-full opacity-50"
          viewBox="0 0 1200 600"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="secondWave" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.5" />
              <stop offset="50%" stopColor="#60A5FA" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#93C5FD" stopOpacity="0.1" />
            </linearGradient>
          </defs>
          <path
            d="M0,0 L1200,0 L1200,300
               C1050,350 950,280 850,320
               C750,360 650,290 550,330
               C450,370 350,300 250,340
               C150,380 50,310 0,350 Z"
            fill="url(#secondWave)"
          />
        </svg>

        {/* Fourth Wave Layer - Creating Wavy Bottom Edge */}
        <svg
          className="absolute bottom-0 left-0 w-full h-32"
          viewBox="0 0 1200 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            d="M0,120 L1200,120 L1200,80
               C1050,30 950,100 850,60
               C750,20 650,90 550,50
               C450,10 350,80 250,40
               C150,0 50,70 0,30 Z"
            fill="#f4f8fd"
          />
        </svg>
      </div>

      {/* Main Content Container - Positioned strictly within first wave area */}
      <div className="relative z-20 mx-auto px-4 max-w-7xl">
        {/* Content positioned to stay within first wave boundaries */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between items-center justify-start h-[180px] lg:h-[200px] md:h-[190px] sm:h-[170px]">
          {/* Text Content */}
          <div
            className={`w-full lg:w-auto lg:flex-1 pl-0 lg:pl-14 pt-4 lg:pt-6 md:pt-5 sm:pt-3 transition-all duration-700 ease-out ${
              isVisible
                ? "translate-y-0 opacity-100"
                : "translate-y-5 opacity-0"
            }`}
          >
            {/* Main Title - Constrained within first wave */}
            <h1 className="text-3xl sm:text-4xl md:text-4xl lg:text-5xl xl:text-5xl font-extrabold leading-[1.1] text-white mb-2 lg:mb-3 md:mb-2 sm:mb-2 font-['Poppins',sans-serif] text-center lg:text-left md:text-center sm:text-center">
              Ready to Start Your Journey!!!
            </h1>

            {/* Subtitle - Positioned within first wave */}
            <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-xl font-normal leading-[1.3] text-white/90 max-w-3xl font-['Poppins',sans-serif] text-center lg:text-left md:text-center sm:text-center">
              Explore our current openings and find the perfect opportunity to
              advance your career with a company that values innovation, growth,
              and excellence.
            </p>
          </div>

          {/* Button positioned at the right end */}
          <div
            className={`w-full lg:w-auto flex justify-center lg:justify-end lg:items-start pt-4 lg:pt-6 md:pt-5 sm:pt-3 lg:pr-14 transition-all duration-700 ease-out ${
              isVisible
                ? "translate-y-0 opacity-100"
                : "translate-y-5 opacity-0"
            }`}
          >
            {/* Mobile + Tablet Button */}
            <button
              onClick={() =>
                document
                  .getElementById("job-listings")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="lg:hidden px-6 py-2.5 bg-gradient-to-r from-orange-600 to-yellow-600 text-white font-semibold rounded-full hover:opacity-90 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#1719A8] text-sm whitespace-nowrap"
              aria-label="View available job positions (mobile)"
            >
              View Open Positions
            </button>

            {/* Desktop Button */}
            <button
              onClick={() =>
                document
                  .getElementById("job-listings")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="hidden lg:inline-flex px-8 py-3 bg-white text-[#1719A8] font-semibold rounded-full hover:bg-gray-100 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#1719A8] text-base whitespace-nowrap"
              aria-label="View available job positions (desktop)"
            >
              View Open Positions
            </button>
          </div>
        </div>
      </div>

      {/* Decorative elements positioned within first wave area */}
      <div
        className="absolute top-[120px] lg:top-[130px] md:top-[125px] sm:top-[115px] left-0 right-0 z-15"
        aria-hidden="true"
      >
        <div className="max-w-7xl mx-auto px-4 relative h-[120px] lg:h-[140px] md:h-[130px] sm:h-[110px]">
          {/* Floating particles constrained within first wave */}
          <div className="absolute top-4 right-8 lg:right-12 md:right-10 sm:right-6 w-2 h-2 lg:w-3 lg:h-3 md:w-2.5 md:h-2.5 sm:w-2 sm:h-2 bg-white/40 rounded-full animate-pulse"></div>
          <div className="absolute top-8 lg:top-10 md:top-9 sm:top-7 left-8 lg:left-12 md:left-10 sm:left-6 w-1.5 h-1.5 lg:w-2.5 lg:h-2.5 md:w-2 md:h-2 sm:w-1.5 sm:h-1.5 bg-white/30 rounded-full animate-bounce"></div>
          <div className="absolute top-12 lg:top-16 md:top-14 sm:top-10 right-1/4 w-2 h-2 lg:w-3 lg:h-3 md:w-2.5 md:h-2.5 sm:w-1.5 sm:h-1.5 bg-white/50 rounded-full animate-ping"></div>
        </div>
      </div>

      {/* Custom Animation Styles */}
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        /* Ensure text remains readable and positioned within first wave */
        @media (max-width: 640px) {
          h1 {
            text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
          }
        }

        @media (min-width: 641px) and (max-width: 1024px) {
          h1 {
            text-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
          }
        }

        @media (min-width: 1025px) {
          h1 {
            text-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
          }
        }

        /* Additional constraint to ensure content stays in first wave */
        .first-wave-content {
          max-height: 200px;
          overflow: hidden;
        }
      `}</style>
    </section>
  );
}

export default CareersHeroSection;
